import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { getProductionLabel, MILL_1 } from "@/setup/mill-config/mill-config";
import { routeStackerProduction } from "@/setup/routes/stacker-pulpit.routes";
import { pageBgColor } from "@/utils/theme";
import {
	ErrorModal,
	getEquivalentShiftForAsset,
	getMomentTz,
	HelmetDexteel,
	ShiftDayNavigatorControl,
	useHasProfile,
	useShiftNavigator,
	useUserContext,
	useUTLSettingsContext,
} from "@dexteel/mesf-core";
import { Add } from "@mui/icons-material";
import { Box, Button, Grid2, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { get } from "lodash-es";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { StackerBundlesGrid } from "./components/bundles/StackerBundlesGrid";
import { StackerPiecesGrid } from "./components/pieces/StackerPiecesGrid";
import { StackerHeatPanel } from "./components/StackerHeatPanel";
import { StackerShiftRemarkField } from "./components/StackerShiftRemarkField";
import {
	STACKER_BUNDLE_DRAFT_INITIAL,
	STACKER_PIECES_DRAFT_INITIAL,
	StackerBundle,
	StackerBundleDraft,
} from "./models/StackerBundle";
import {
	deleteStackerBundle,
	getStackerReport,
	StackerReport,
	upsertStackerBundle,
	upsertStackerShiftRemark,
} from "./repositories/StackerPulpitRepository";

const moment = getMomentTz();

export const useStackerReport = ({
	shiftId,
}: {
	shiftId: number | undefined;
}) => {
	const {
		state: { defaultAreaId },
	} = useUserContext();

	return useQuery<StackerReport, Error>({
		queryKey: ["stacker-report", shiftId, defaultAreaId],
		queryFn: ({ signal }) =>
			getStackerReport(
				{ shiftId: shiftId || 0, defaultAreaId: Number(defaultAreaId) },
				signal,
			),
		enabled: !!shiftId,
	});
};

export const StackerPulpitPage = () => {
	const {
		state: { defaultAreaId },
	} = useUserContext();

	const { state } = useUTLSettingsContext();
	const graceMinutes = Number(
		get(state, `settings.STACKER_EDIT_GRACE_MINUTES`, 15),
	);

	const assetId = Number(defaultAreaId);
	const isM1 = assetId === MILL_1.id;
	const productionLabel = getProductionLabel(defaultAreaId);

	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { shiftId: shiftIdParam } = useParams();
	const hasProfile = useHasProfile();
	const { shiftInfo, getShiftDataFromAPI } = useShiftNavigator();

	const shiftBelongsToAsset = (shiftInfo?.AssetId ?? assetId) === assetId;
	const currentShiftId = shiftBelongsToAsset
		? shiftInfo?.CurrentShiftId
		: undefined;
	const shiftEndTs = shiftInfo?.CurrentEnd
		? new Date(shiftInfo.CurrentEnd).getTime()
		: null;

	const isOperatorOnly =
		hasProfile("Operator Profile") &&
		!hasProfile("Clerk Profile") &&
		!hasProfile("Supervisor Profile") &&
		!hasProfile("SubAdmin Profile") &&
		!hasProfile("admin Profile");

	const [editingId, setEditingId] = useState<number | null>(null);
	const [draftRow, setDraftRow] = useState<StackerBundleDraft | null>(null);
	const [error, setError] = useState("");
	const [notificationMessage, setNotificationMessage] = useState("");
	const [bundleToDelete, setBundleToDelete] = useState<StackerBundle | null>(
		null,
	);

	const {
		data: report,
		isLoading,
		isFetching,
		isError,
		error: e,
	} = useStackerReport({
		shiftId: currentShiftId,
	});

	const bundles = report?.bundles || [];
	const remark = report?.remark ?? null;
	const heat = report?.heat ?? null;

	const editGraceMs = graceMinutes * 60 * 1000;

	const isReadOnly =
		isOperatorOnly &&
		shiftEndTs !== null &&
		moment.now() > shiftEndTs + editGraceMs;

	const upsertBundle = useMutation(upsertStackerBundle, {
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["stacker-report", currentShiftId],
			});
			setNotificationMessage("The row was saved successfully");
		},
		onError: (error: Error) => setError(error.message),
	});
	const deleteBundle = useMutation(deleteStackerBundle, {
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["stacker-report", currentShiftId],
			});
			setNotificationMessage("The row was deleted successfully");
		},
		onError: (error: Error) => setError(error.message),
	});
	const upsertRemark = useMutation(upsertStackerShiftRemark, {
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["stacker-report", currentShiftId],
			});
			setNotificationMessage("Remark saved successfully");
		},
		onError: (error: Error) => setError(error.message),
	});

	const isBundleMutating = upsertBundle.isPending || deleteBundle.isPending;
	// Footer totals reflect accumulated data across all rows; show a loader on
	// them while a save is in flight or the report is refetching the fresh data.
	const isTotalsLoading = isBundleMutating || isFetching;

	const startEdit = (b: StackerBundle) => {
		if (isReadOnly) return;
		setEditingId(b.StackerBundleId);

		if (isM1) {
			setDraftRow({
				...STACKER_PIECES_DRAFT_INITIAL,
				StackerBundleId: b.StackerBundleId,
				ShiftId: b.ShiftId,
				SectionId: b.SectionId,
				GradeId: b.GradeId,
				HeatId: b.HeatId,
				LengthInches: b.LengthInches,
				GoodPieces: b.GoodPieces ?? 0,
				TotalWeight: b.TotalWeight,
				ScrapPieces: b.ScrapPieces ?? null,
				ScrapReason: b.ScrapReason,
			});
			return;
		}

		const auto = (b.GoodBundles || 0) * (b.GoodPiecesPerBundle || 0);
		setDraftRow({
			StackerBundleId: b.StackerBundleId,
			ShiftId: b.ShiftId,
			SectionId: b.SectionId,
			GradeId: b.GradeId,
			HeatId: b.HeatId,
			LengthInches: b.LengthInches,
			GoodBundles: b.GoodBundles,
			GoodPiecesPerBundle: b.GoodPiecesPerBundle,
			TotalPieces: b.TotalPieces,
			TotalPiecesIsManual: b.TotalPieces !== auto,
			TotalWeight: b.TotalWeight,
			ScrapBundles: b.ScrapBundles,
			ScrapPiecesPerBundle: b.ScrapPiecesPerBundle,
			ScrapReason: b.ScrapReason,
		});
	};

	const startAdd = () => {
		if (!currentShiftId || isReadOnly) return;
		setEditingId(null);
		setDraftRow({
			...(isM1 ? STACKER_PIECES_DRAFT_INITIAL : STACKER_BUNDLE_DRAFT_INITIAL),
			ShiftId: currentShiftId,
		});
	};

	const handleSaveDraft = (draft: StackerBundleDraft) => {
		if (draftRow) {
			upsertBundle.mutate({ draft, defaultAreaId: Number(defaultAreaId) });
		}
		setDraftRow(null);
		setEditingId(null);
	};

	const quickSaveBundle = (
		bundle: StackerBundle,
		patch: {
			GoodPieces: number;
			ScrapPieces: number | null;
			TotalWeight: number | null;
		},
	) => {
		if (isReadOnly) return;
		upsertBundle.mutate({
			draft: {
				...STACKER_PIECES_DRAFT_INITIAL,
				StackerBundleId: bundle.StackerBundleId,
				ShiftId: bundle.ShiftId,
				SectionId: bundle.SectionId,
				GradeId: bundle.GradeId,
				HeatId: bundle.HeatId,
				LengthInches: bundle.LengthInches,
				GoodPieces: patch.GoodPieces,
				TotalWeight: patch.TotalWeight,
				ScrapPieces: patch.ScrapPieces,
				ScrapReason: bundle.ScrapReason,
			},
			defaultAreaId: Number(defaultAreaId),
		});
	};

	const cancelEdit = () => {
		setEditingId(null);
		setDraftRow(null);
	};

	useEffect(() => {
		if (shiftInfo) {
			const shiftId = get(shiftInfo, "CurrentShiftId", null);
			navigate(`${routeStackerProduction}/${shiftId}`, { replace: true });
			cancelEdit();
		}
	}, [shiftInfo]);

	useEffect(() => {
		if (!assetId) return;
		(async () => {
			if (shiftIdParam) {
				const equivalent = await getEquivalentShiftForAsset(
					Number(shiftIdParam),
					assetId,
				);
				if (equivalent) {
					await getShiftDataFromAPI(null, equivalent.ShiftId, assetId);
					return;
				}
			}
			const productionDate = shiftInfo?.CurrentProductionDate ?? null;
			await getShiftDataFromAPI(productionDate, 0, assetId);
		})();
	}, [assetId]);

	useEffect(() => {
		if (isError && e) {
			setError(e.message);
		}
	}, [e, isError]);

	return (
		<>
			<HelmetDexteel title={productionLabel} />
			<Box
				sx={{
					backgroundColor: pageBgColor,
					minHeight: "calc(100vh - 70px)",
				}}
			>
				<Grid2 container size={{ md: 12, xs: 12 }} spacing={1} px={2} pt={2}>
					<Grid2
						container
						size={{ md: 12, xs: 12 }}
						alignItems="center"
						justifyContent="space-between"
						spacing={1}
					>
						<Grid2 size={{ xs: "auto" }}>
							<Typography variant="h5" sx={{ fontWeight: 600 }}>
								{productionLabel}
							</Typography>
						</Grid2>
						<Grid2 size={{ xs: "auto" }}>
							<StackerHeatPanel heat={heat} />
						</Grid2>
					</Grid2>

					<Grid2
						container
						size={{ md: 12, xs: 12 }}
						alignItems="center"
						justifyContent="space-between"
						spacing={1}
					>
						<Grid2 size={{ xs: "auto" }}>
							<ShiftDayNavigatorControl
								preventInit
								assetId={Number(defaultAreaId)}
							/>
						</Grid2>
						<Grid2
							size={{ xs: "auto" }}
							sx={{
								display: "flex",
								flexDirection: "column",
								alignItems: "flex-end",
								gap: 0.5,
							}}
						>
							<Button
								variant="contained"
								startIcon={<Add />}
								onClick={startAdd}
								disabled={
									!currentShiftId ||
									draftRow !== null ||
									editingId !== null ||
									isBundleMutating ||
									isReadOnly
								}
							>
								Add Row
							</Button>
							{isReadOnly && (
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 0.5,
									}}
								>
									<Typography
										variant="caption"
										color="textDisabled"
										sx={{ whiteSpace: "nowrap" }}
									>
										The editing window for this shift has closed - you can no
										longer add or edit rows.
									</Typography>
								</Box>
							)}
						</Grid2>
					</Grid2>

					<Grid2 size={{ md: 12, xs: 12 }}></Grid2>

					<Grid2 size={{ md: 12, xs: 12 }}>
						{isLoading ? (
							<Box sx={{ height: 200 }}>
								<CenteredLazyLoading />
							</Box>
						) : isM1 ? (
							<StackerPiecesGrid
								rows={bundles}
								shiftId={currentShiftId || 0}
								draftRow={draftRow}
								editingId={editingId}
								isMutating={isBundleMutating}
								isTotalsLoading={isTotalsLoading}
								readOnly={isReadOnly}
								onStartEdit={startEdit}
								onCancelEdit={cancelEdit}
								onDraftChange={setDraftRow}
								onSaveDraft={handleSaveDraft}
								onDelete={(s) => setBundleToDelete(s)}
								onQuickSave={quickSaveBundle}
							/>
						) : (
							<StackerBundlesGrid
								rows={bundles}
								shiftId={currentShiftId || 0}
								draftRow={draftRow}
								editingId={editingId}
								isMutating={isBundleMutating}
								isTotalsLoading={isTotalsLoading}
								readOnly={isReadOnly}
								onStartEdit={startEdit}
								onCancelEdit={cancelEdit}
								onDraftChange={setDraftRow}
								onSaveDraft={handleSaveDraft}
								onDelete={(s) => setBundleToDelete(s)}
							/>
						)}
					</Grid2>

					<Grid2 size={{ md: 12, xs: 12 }} sx={{ mt: 2 }}>
						<StackerShiftRemarkField
							remark={remark}
							disabled={!currentShiftId || isReadOnly}
							isMutating={upsertRemark.isLoading}
							onSave={(comment) => {
								if (!currentShiftId) return;
								upsertRemark.mutate({
									shiftId: currentShiftId,
									comment,
									defaultAreaId: Number(defaultAreaId),
								});
							}}
						/>
					</Grid2>
				</Grid2>
			</Box>

			<ConfirmationDialogRaw
				title="Confirm Deletion"
				message={
					bundleToDelete
						? `Are you sure you want to delete the bundle with section ${bundleToDelete.SectionName}, ${
								bundleToDelete.HeatName
									? `heat ${bundleToDelete.HeatName}, `
									: ""
							}${
								bundleToDelete.GradeName
									? `grade ${bundleToDelete.GradeName}, `
									: ""
							}length ${Math.floor(
								bundleToDelete.LengthInches / 12,
							)}' ${bundleToDelete.LengthInches % 12}" and ${
								bundleToDelete.TotalPieces ?? bundleToDelete.GoodPieces ?? 0
							} total pieces? This action cannot be undone.`
						: ""
				}
				open={!!bundleToDelete}
				onClose={(value?: string) => {
					if (value == "OK") {
						deleteBundle.mutate({
							stackerBundleId: bundleToDelete?.StackerBundleId!,
							defaultAreaId: Number(defaultAreaId),
						});
					}
					setBundleToDelete(null);
				}}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
		</>
	);
};
